import { useState } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import AITradingView, { type TradeSide } from './components/AITradingView';
import BridgeView from './components/BridgeView';
import DashboardView from './components/DashboardView';
import EarnView from './components/EarnView';
import EnterpriseApiView from './components/EnterpriseApiView';
import FarmView from './components/FarmView';
import GovernanceView from './components/GovernanceView';
import Header, { type NavView } from './components/Header';
import LaunchpadView from './components/LaunchpadView';
import LendingView from './components/LendingView';
import MarketSidebar from './components/MarketSidebar';
import PoolsView from './components/PoolsView';
import PortfolioView from './components/PortfolioView';
import PredictionView from './components/PredictionView';
import MarketTickerTape from './components/MarketTickerTape';
import PriceChart from './components/PriceChart';
import RwaView from './components/RwaView';
import StakingView from './components/StakingView';
import SwapPanel from './components/SwapPanel';
import WalletModal from './components/WalletModal';
import { MARKET_PAIRS, type MarketPair } from './data/mockMarket';
import { usePairMetrics } from './web3/usePairMetrics';

export default function App() {
  const [selected, setSelected] = useState<MarketPair>(MARKET_PAIRS[0]);
  const [view, setView] = useState<NavView>('Dashboard');
  const [tradeSide, setTradeSide] = useState<TradeSide>('buy');

  const handleAiTrade = (symbol: string, side: TradeSide) => {
    const pair = MARKET_PAIRS.find((p) => p.base === symbol && p.quote === 'USDT');
    if (!pair) return;
    setSelected(pair);
    setTradeSide(side);
    setView('Swap');
  };

  return (
    <ErrorBoundary>
    <div className="h-screen flex flex-col bg-abyss-950">
      <WalletModal />
      <Header active={view} onNavigate={setView} />
      {view === 'Pools' ? (
        <PoolsView />
      ) : view === 'Farm' ? (
        <FarmView />
      ) : view === 'Governance' ? (
        <GovernanceView />
      ) : view === 'Earn' ? (
        <EarnView />
      ) : view === 'AI Trading' ? (
        <AITradingView onTrade={handleAiTrade} />
      ) : view === 'RWA' ? (
        <RwaView />
      ) : view === 'Launchpad' ? (
        <LaunchpadView />
      ) : view === 'Staking' ? (
        <StakingView />
      ) : view === 'Lending' ? (
        <LendingView />
      ) : view === 'Prediction Market' ? (
        <PredictionView />
      ) : view === 'Bridge' ? (
        <BridgeView />
      ) : view === 'Enterprise API' ? (
        <EnterpriseApiView />
      ) : view === 'Portfolio' ? (
        <PortfolioView />
      ) : view === 'Dashboard' ? (
        <DashboardView onNavigate={setView} />
      ) : (
        <TradeScreen selected={selected} onSelect={setSelected} tradeSide={tradeSide} />
      )}
    </div>
    </ErrorBoundary>
  );
}

// The default-visible trading screen: a live market ticker tape on top of the
// classic sidebar / chart / swap-panel layout, so the first thing a visitor sees
// reads like a real exchange rather than a bare swap widget.
function TradeScreen({
  selected,
  onSelect,
  tradeSide,
}: {
  selected: MarketPair;
  onSelect: (pair: MarketPair) => void;
  tradeSide: TradeSide;
}) {
  const { metrics, loading } = usePairMetrics();

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-4 pt-3 shrink-0">
        <MarketTickerTape metrics={metrics} loading={loading} />
      </div>
      <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-y-auto lg:overflow-hidden">
        <MarketSidebar selected={selected.id} onSelect={onSelect} />
        <main className="flex flex-col flex-shrink-0 min-h-[420px] lg:flex-1 lg:min-h-0">
          <PriceChart pair={selected} />
        </main>
        <SwapPanel
          // remount whenever the pair or AI-suggested direction changes so the
          // from/to tokens re-initialize instead of keeping stale state
          key={`${selected.id}:${tradeSide}`}
          pair={selected}
          initialFrom={tradeSide === 'sell' ? selected.base : undefined}
          initialTo={tradeSide === 'sell' ? selected.quote : undefined}
        />
      </div>
    </div>
  );
}
